import constants from "./constants";

// Form-factor classifier for the post-login mobile-vs-desktop routing decision.
// The camera/scanner INPUT choice is the mobile app's job (useDeviceCaps); this only answers
// "mobile experience or desktop experience?", auto-resolving the confident cases so the manual
// device picker is needed only on genuine ambiguity (e.g. a touchscreen laptop). Scanner
// handhelds are resolved earlier, at login (useLoginDeviceDetect) — not re-detected here.

function gatherDeviceSignals() {
   const ua = navigator.userAgent || "";
   return {
      mobileUa: /Android|iPhone|iPad|iPod|Windows Phone|Mobile/i.test(ua),
      coarsePointer: window.matchMedia ? window.matchMedia("(pointer: coarse)").matches : false,
      finePointer: window.matchMedia ? window.matchMedia("(pointer: fine)").matches : false,
      maxTouchPoints: navigator.maxTouchPoints || 0
   };
}

// Returns a DEVICE_TYPES entry when the form factor is unambiguous, otherwise null
// (caller should fall back to the manual picker). Entries are found by their flags so the
// result is robust to DEVICE_TYPES ordering.
function classifyDevice(signals) {
   const s = signals || gatherDeviceSignals();
   const mobile = constants.DEVICE_TYPES.find(d => d.isMobile && !d.hasScanner);   // "Mobile (Phone/Tablet/etc)"
   const desktop = constants.DEVICE_TYPES.find(d => !d.isMobile && !d.hasScanner); // "Desktop or Laptop"
   // Confident mobile: a phone/tablet UA, or a touch-primary device with no mouse.
   if (s.mobileUa || (s.coarsePointer && s.maxTouchPoints > 1 && !s.finePointer))
      return mobile;
   // Confident desktop: a mouse-driven device with no touch and no mobile UA.
   if (s.finePointer && s.maxTouchPoints === 0 && !s.mobileUa)
      return desktop;
   // Ambiguous (e.g. touchscreen laptop) — let the user choose.
   return null;
}

// Single source for persisting a chosen/detected device and routing to its experience.
// Shared by the post-login auto-classify (Home) and the manual picker (MobileDeviceSelect)
// so the two can never drift. State is set before navigating so the destination route reads
// the updated context.
function applyDevice(device, userCtx, navigate) {
   userCtx.setOnMobile(device.isMobile);
   userCtx.setSavedDevice(device);
   if (device.isMobile) {
      userCtx.setPinned(true);
      navigate(constants.ENDPOINTS.mobileHome);
   }
   else {
      navigate("/");
   }
}

function isNoScannerMobile(ua) {
   // Apple phones/iPods never have a built-in barcode scanner and their UA still reliably reports
   // "iPhone"/"iPod", so they can be classified no-scanner without prompting. iPad is intentionally
   // excluded: iPadOS reports a desktop-class UA, so it cannot be detected reliably here.
   return /iPhone|iPod/i.test(ua || navigator.userAgent || "");
}

const deviceDetect = { gatherDeviceSignals, classifyDevice, applyDevice, isNoScannerMobile };
export default deviceDetect;
